import { useAuthentication } from '@context/businessContainer';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useUniqueToast } from '@hooks/useUniqueToast';
import {
  createDeliveryBarContext,
  DeliveryBarContent as DeliveryBarContentBase,
  DeliveryBarMachineContext,
  formatShippingMethod,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import { useLocalStorage } from '@monofront/business/hooks';
import {
  DELIVERY_BAR_EXTERNAL_SERVICES,
  EVENTS,
} from '@monofront/business/stateMachines/deliveryBar';
import { customerCRUD } from '@services/businessContainer';
import { useTranslations } from 'next-intl';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EmptyObject } from '@monofront/types';
import { useLocalOrRemoteCustomer } from '@hooks/useLocalOrRemoteCustomer';
import { ABTEST_DELIVERY_BAR } from '@monofront/business/constants';

const {
  DeliverBarContextProvider: DeliverBarContextProviderBase,
  useDeliveryBar: useBaseDeliveryBar,
} = createDeliveryBarContext({
  useLocalStorage,
  useAuthentication,
  useShoppingCart,
  customerCRUD,
});

export type DeliveryBarContent = DeliveryBarContentBase & {
  isSidePanelOpen: boolean;
  setIsSidePanelOpen: (isOpen: boolean) => void;
};

// extends DeliveryBarContext with ecommerce only concerns (sidepanel, ...)
const DeliveryBarContext = createContext<DeliveryBarContent | null>(null);

const ResetHandler = ({ isSidePanelOpen }: { isSidePanelOpen: boolean }) => {
  const { send } = DeliveryBarMachineContext.useActorRef();
  const { resolvedCustomer: customer } = useLocalOrRemoteCustomer();
  const prevIsSidePanelOpen = useRef(isSidePanelOpen);

  useEffect(() => {
    // Rehydrate when side panel opens (transitions from false to true)
    if (!prevIsSidePanelOpen.current && isSidePanelOpen) {
      send({
        type: EVENTS.GO_TO_STATE_INITIAL_STATE_RESOLVER,
        input: {
          address: customer?.address || null,
          shippingMethod: customer?.preferredShippingMethod
            ? formatShippingMethod(customer.preferredShippingMethod)
            : null,
          eligibleShippingMethods: [],
        },
      });
    }

    prevIsSidePanelOpen.current = isSidePanelOpen;
  }, [isSidePanelOpen, send, customer]);

  return null;
};

// Inner component that has access to the base context
const SidePanelContextProvider = ({
  children,
  isSidePanelOpen,
  setIsSidePanelOpen,
}: {
  children: React.ReactNode;
  isSidePanelOpen: boolean;
  setIsSidePanelOpen: (isOpen: boolean) => void;
}) => {
  const baseContext = useBaseDeliveryBar();

  const context = useMemo<DeliveryBarContent>(
    () =>
      ({
        ...baseContext,
        isSidePanelOpen,
        setIsSidePanelOpen,
      } as DeliveryBarContent),
    [baseContext, isSidePanelOpen, setIsSidePanelOpen],
  );

  return (
    <DeliveryBarContext.Provider value={context}>
      <ResetHandler isSidePanelOpen={isSidePanelOpen} />
      {children}
    </DeliveryBarContext.Provider>
  );
};

const DeliverBarContextProvider = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: DeliveryBarContent | EmptyObject;
}) => {
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const isDeliveryBarABTestEnabled = useFeatureIsOn(ABTEST_DELIVERY_BAR);
  const { toast } = useUniqueToast({ toastId: 'delivery-bar-success-toast' });
  const tToast = useTranslations('DeliveryBar.Toasts');

  const actionsCallbacks = {
    [DELIVERY_BAR_EXTERNAL_SERVICES.validateFormData]: {
      success: () => {
        toast(tToast('shippingMethodSelected'));
        setIsSidePanelOpen(false);
      },
      error: () => {
        setIsSidePanelOpen(false);
      },
    },
  };

  return (
    <DeliverBarContextProviderBase
      content={content}
      actionsCallbacks={actionsCallbacks}
    >
      {isDeliveryBarABTestEnabled ? (
        <SidePanelContextProvider
          isSidePanelOpen={isSidePanelOpen}
          setIsSidePanelOpen={setIsSidePanelOpen}
        >
          {children}
        </SidePanelContextProvider>
      ) : (
        children
      )}
    </DeliverBarContextProviderBase>
  );
};

const NOOP_DELIVERY_BAR_CONTEXT: DeliveryBarContent = {
  isSidePanelOpen: false,
  setIsSidePanelOpen: () => {},
  addressFormContent: { slices: null },
  shippingMethodSelectionContent: {
    slices: null,
    slices1: null,
    shippingMethods: null,
  },
  waitingProduct: undefined,
  setWaitingProduct: () => {},
};

const useDeliveryBar = (): DeliveryBarContent => {
  const context = useContext(DeliveryBarContext);

  if (!context) {
    return NOOP_DELIVERY_BAR_CONTEXT;
  }

  return context;
};

export { DeliverBarContextProvider, useDeliveryBar };
